import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import { isDispatcherUrl } from 'modules/utils/UrlUtils';
import Config from 'react-native-config';
import { Store } from 'redux';
import { ReduxAxiosRequestConfig } from 'redux-axios-middleware';
import DeviceInfoServices from 'services/DeviceInfoServices';
import { currentAccountConsumerSelector } from 'store/account/selectors';
import { RootState } from 'store/types';
import { DFDHeaders } from '../../serverTypes/authentication';
import * as appSettingsSelectors from '../appSettings/selectors';
import * as authSelectors from '../authentication/selectors';
import * as notificationsSelectors from '../notifications/selectors';
import interceptorRefreshTokenManager from './refreshInterceptorAlertManager';
import { dfdRefreshTokenManager } from './RefreshTokenManager';

const createCancelConfig = (config: ReduxAxiosRequestConfig) => ({
  ...config,
  cancelToken: new axios.CancelToken(cancel => cancel('Cancel repeated request'))
});

const dfdTokenInterceptor = async (store: Store<RootState>, config: ReduxAxiosRequestConfig) => {
  const state = store.getState();
  let dfdAccessToken = authSelectors.dfdAccessTokenSelector(state);

  if (!dfdAccessToken) {
    return config;
  }

  const shouldRefresh = authSelectors.shallRefreshDfdToken(state);

  if (shouldRefresh) {
    const auth = await dfdRefreshTokenManager.refresh(store);

    if (!auth) {
      interceptorRefreshTokenManager.logoutAndAlert(store);
      return createCancelConfig(config);
    }

    if (auth && auth.dfdAuth && auth.dfdAuth.accessToken) {
      dfdAccessToken = auth.dfdAuth.accessToken;
    }
  }

  if (config.reduxSourceAction.payload.client !== 'triage') {
    // eslint-disable-next-line no-param-reassign
    config.headers = {
      Authorization: `Bearer ${dfdAccessToken}`,
      ...config.headers
    };
  }

  return config;
};

const fhirHeaderInterceptor = async (store: Store<RootState>, config: ReduxAxiosRequestConfig) => {
  const state = store.getState();
  const fhirAuth = authSelectors.tpsFhirStatusSelector(state);
  if (!fhirAuth) {
    return config;
  }

  const consumer = currentAccountConsumerSelector(state);
  const { consumerId = '', cernerPersonId = '' } = consumer;

  /*
   * Headers are configured based on three scenarios:
   * 1. Initial app dependencies do not include the headers below (identified by an empty consumer object)
   * 2. All subsequent calls will include the headers below
   * 3. Send an empty string for any header field that does not exist (mandatory)
   */
  if (!isEmpty(consumer)) {
    config.headers = {
      'x-patient-id': cernerPersonId,
      'x-dfd-consumer-id': consumerId,
      ...config.headers
    };
  }

  return config;
};

const apiKeyInterceptor = ({ getState }: Store<RootState>, config: ReduxAxiosRequestConfig) => {
  const apiKey = appSettingsSelectors.apiKeySelector(getState());

  if (!apiKey) {
    return config;
  }

  const url = config?.baseURL || '';
  if (process.env.BUILD_ENV !== 'PROD' && url.includes('localhost')) {
    config.headers[DFDHeaders.apiKeyLocalhost] = apiKey;
  } else {
    config.headers = {
      ...config.headers,
      apikey: apiKey
    };
  }

  return config;
};

const deviceIdInterceptor = (_: Store<RootState>, config: ReduxAxiosRequestConfig) => {
  const deviceId = DeviceInfoServices.getDeviceId();

  if (deviceId) {
    config.headers = {
      'x-device-id': deviceId,
      ...config.headers
    };
  }

  return config;
};

const hipaaPatientIdInterceptor = (
  { getState }: Store<RootState>,
  config: ReduxAxiosRequestConfig
) => {
  const patientId = notificationsSelectors.notificationsHipaaPatientIdSelector(getState());
  const { consumerId } = currentAccountConsumerSelector(getState());

  if (config.reduxSourceAction.payload.client === 'hipaa' && patientId && consumerId) {
    // eslint-disable-next-line no-param-reassign
    config.headers = {
      'x-patient-id': patientId,
      'x-dfd-consumer-id': consumerId,
      ...config.headers
    };
  }

  return config;
};

const nevadaInterceptor = ({ getState }: Store<RootState>, config: ReduxAxiosRequestConfig) => {
  if (Config.HCPNV_ENABLED && isDispatcherUrl(config?.baseURL || '')) {
    config.headers[DFDHeaders.mhpNVIdxPid] = '1';
    config.headers[DFDHeaders.mhpTWmrn] = '1';
  }

  return config;
};

const requestInterceptors = [
  dfdTokenInterceptor,
  apiKeyInterceptor,
  deviceIdInterceptor,
  hipaaPatientIdInterceptor,
  fhirHeaderInterceptor,
  nevadaInterceptor
];

export default requestInterceptors;
